import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { LayoutDebugOverlayPlan } from "./dbg-layout-overlay-plan.ts";
import { LayoutDebugOverlayRenderer } from "./dbg-layout-overlay-renderer.ts";
import { GeometrySnapshot, LayoutEngine } from "@tento-lona/sheets/platform";
import { SheetViewModel } from "@tento-lona";
import type { RowLocalId, Option } from "@tento-lona/sheets";
import { SheetDebugOverlay } from "@tento-ui/components/sheet/dbg-sheet-overlay";
import { SheetDelegate } from "../sheet-delegate.ts";
import { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { ColumnTimescale } from "@tento-lona/internal-ui";
import type { SheetBinder } from "../sheet-index-renderer.ts";

export class LayoutDebugOverlayController {
  static readonly #FIXED_OWNER = "layout-debug-overlay";
  #enabled = false;
  #specs = new Map<string, LayoutDebugOverlayPlan.Spec>();
  #fixedRows = new Map<string, RowLocalId>();
  #staticSpecs = new Map<string, LayoutDebugOverlayPlan.Spec>();
  #renderer: Option<LayoutDebugOverlayRenderer> = null;

  readonly #getSheet: () => Option<SheetViewModel.Surface<SheetBinder>>;
  readonly #handle: SheetDelegate;
  readonly #layout: SheetLayoutPort;
  constructor(
    layout: SheetLayoutPort,
    handle: SheetDelegate,
    getSheet: () => Option<SheetViewModel.Surface<SheetBinder>>,
  ) {
    this.#layout = layout;
    this.#handle = handle;
    this.#getSheet = getSheet;
  }

  setEnabled(enabled: boolean): void {
    this.#enabled = enabled;
    if (!enabled) {
      this.clear();
      return;
    }
    this.rebind();
  }

  clear(): void {
    this.#handle.fixedChild.clearFixedChildrenByOwner(
      LayoutDebugOverlayController.#FIXED_OWNER,
    );
    this.#fixedRows.clear();

    if (this.#renderer && this.#staticSpecs.size > 0) {
      this.#renderer.clear(this.#staticSpecs);
    }

    this.#staticSpecs.clear();
    this.#specs.clear();
  }

  rebind(): void {
    if (!this.#enabled) {
      this.clear();
      return;
    }

    const sheet = this.#getSheet();
    if (!sheet) {
      this.clear();
      return;
    }

    const renderedElements = this.#layout.columns.renderedElements();
    const renderedColumnsByOffset = new Map<
      number,
      LayoutDebugOverlayPlan.RenderedColumn
    >();
    for (const { column, widthPx } of renderedElements) {
      const resolved = sheet.indexRenderer.mapper
        .resolveKey(column)
        .castToDefault();
      if (!resolved) continue;
      if (renderedColumnsByOffset.has(resolved.offset)) continue;
      renderedColumnsByOffset.set(resolved.offset, {
        resolved,
        widthPx,
      });
    }

    const renderedColumns = [...renderedColumnsByOffset.values()].sort(
      (a, b) => a.resolved.offset - b.resolved.offset,
    );

    if (renderedColumns.length === 0) {
      const visibleIndices = this.#layout.columns.visibleIndices();
      const start = visibleIndices.start;
      const end = visibleIndices.end;
      if (start == null || end == null) {
        return;
      }

      for (let index = start; index <= end; index++) {
        const resolved = sheet.indexRenderer.mapper.resolveKeyToDefault(
          ColumnIndex.Key.fromIndex(index),
          "lower",
        );
        // The last raw reach-through in this file: it walked the layout
        // element into its scroller's engine to read one width. The port's own
        // answer is the same call, one hop shorter.
        const widthPx =
          this.#layout.scroll.cellWidthAt(index) ??
          this.#handle.state.resolvedWidthPx;
        renderedColumns.push({
          resolved,
          widthPx,
        });
      }
    }

    const rows: LayoutDebugOverlayPlan.RenderedRow[] = [];
    for (const [rowIdx, row] of sheet.joinedTree.flattened.rows.entries()) {
      const layout = sheet.layout.rowLayout.get(row.id);
      if (!layout) {
        continue;
      }
      rows.push({
        rowIdx,
        id: row.id,
        timescale: row.timescaleSetting,
        spannable: row.layoutSpec(sheet.timescale).spannable,
        location: sheet.joinedTree.isInVisibleHeader(row.id)
          ? "header"
          : "body",
        layout: GeometrySnapshot.cellLayoutOf(layout),
      });
    }

    if (renderedColumns.length === 0 || rows.length === 0) {
      // During viewport transition phases, rendered columns can be temporarily
      // empty. Keep existing overlays instead of diffing to empty and causing
      // flicker/disappearing debug cells.
      return;
    }

    const engine = new LayoutEngine({
      getColumnOffsetPx: (index: number) =>
        this.#handle.state.getColumnOffsetPx(index),
      getColumnRangeWidthPx: (start: number, end: number) =>
        this.#handle.state.getColumnRangeWidthPx(start, end),
      getSpanColumnCount: (period) =>
        ColumnTimescale.calculateSpanSize(period, sheet.indexRenderer.mapper),
    });

    const builtSpecs = LayoutDebugOverlayPlan.buildSpecs(
      engine,
      {
        timescale: sheet.timescale,
        visibleWidthPx: this.#handle.state.visibleWidthPx ?? 0,
        resolvedWidthPx: this.#handle.state.resolvedWidthPx,
      },
      rows,
      renderedColumns,
    );

    const stabilizedSpecs: LayoutDebugOverlayPlan.Spec[] = builtSpecs;

    const logicalNextMap = new Map(
      stabilizedSpecs.map((spec) => [spec.id, spec]),
    );

    const scopedSpecs = stabilizedSpecs.map((spec) => ({
      ...spec,
      id: this.#scopedOverlayId(sheet.id.toString(), spec.id),
    }));
    this.#applyFixedSpecs(scopedSpecs.filter((spec) => spec.target === "fixed"));

    const staticNext = scopedSpecs.filter((spec) => spec.target === "static");
    const staticPlan = LayoutDebugOverlayPlan.computePlan(
      this.#staticSpecs,
      staticNext,
    );
    this.#getRenderer().apply(staticPlan);
    this.#staticSpecs = staticPlan.next;

    this.#specs = logicalNextMap;
  }

  #applyFixedSpecs(specs: LayoutDebugOverlayPlan.Spec[]): void {
    const byRow = new Map<
      string,
      { rowId: RowLocalId; specs: LayoutDebugOverlayPlan.Spec[] }
    >();
    for (const spec of specs) {
      const key = spec.rowId.toString();
      const entry = byRow.get(key);
      if (entry) {
        entry.specs.push(spec);
        continue;
      }
      byRow.set(key, {
        rowId: spec.rowId,
        specs: [spec],
      });
    }

    for (const [rowKey, localId] of this.#fixedRows) {
      if (byRow.has(rowKey)) continue;
      this.#handle.fixedChild.setFixedChildrenForRow(localId, [], {
        owner: LayoutDebugOverlayController.#FIXED_OWNER,
      });
    }

    for (const [, { rowId, specs: rowSpecs }] of byRow) {
      const children: SheetDelegate.FixedChildSpec[] = rowSpecs.flatMap(
        (spec) => {
          if (spec.anchor.key.type !== "default") {
            return [];
          }

          return [
            {
              id: spec.id,
              x: this.#toFixedX(spec),
              inset: {
                left: spec.inset.left,
                top: spec.inset.top,
              },
              widthBehavior: this.#toFixedWidthBehavior(spec),
              makeElement: () => this.#makeOverlayElement(spec),
              onUpdate: ($element: HTMLElement) => {
                this.#bindOverlayElement($element, spec);
              },
            } satisfies SheetDelegate.FixedChildSpec,
          ];
        },
      );
      this.#handle.fixedChild.setFixedChildrenForRow(rowId, children, {
        owner: LayoutDebugOverlayController.#FIXED_OWNER,
      });
    }

    this.#fixedRows = new Map(
      [...byRow.entries()].map(([rowKey, value]) => [rowKey, value.rowId]),
    );
  }

  #makeOverlayElement(spec: LayoutDebugOverlayPlan.Spec): HTMLElement {
    return SheetDebugOverlay.make().bind({
      label: spec.label,
      color: spec.color,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
    });
  }

  #bindOverlayElement(
    $element: HTMLElement,
    spec: LayoutDebugOverlayPlan.Spec,
  ): void {
    if (!($element instanceof SheetDebugOverlay)) return;
    $element.bind({
      label: spec.label,
      color: spec.color,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
    });
  }

  #getRenderer(): LayoutDebugOverlayRenderer {
    if (!this.#renderer) {
      this.#renderer = new LayoutDebugOverlayRenderer(
        this.#handle.fixedChild,
        this.#handle.staticChild,
      );
    }
    return this.#renderer;
  }

  #scopedOverlayId(sheetId: string, localId: string): string {
    const safeSheet = this.#sanitizeIdPart(sheetId);
    const safeLocal = this.#sanitizeIdPart(localId);
    return `layout-debug-sheet-${safeSheet}-${safeLocal}`;
  }

  #sanitizeIdPart(value: string): string {
    const normalized = value.replace(/[^a-zA-Z0-9_-]/g, "-");
    return normalized.length > 0 ? normalized : "x";
  }

  #toFixedX(
    spec: LayoutDebugOverlayPlan.Spec,
  ): SheetDelegate.FixedChildXPosition {
    if (spec.shape === "default" || spec.shape === "stacked") {
      return {
        type: "resolved-range",
        start: spec.anchor,
        end: new ColumnIndex.Key.Resolved.WithSubindex(
          spec.anchor.key,
          new Rational(1, 1),
        ),
      };
    }

    return {
      type: "resolved-range",
      start: spec.anchor,
    };
  }

  #toFixedWidthBehavior(
    spec: LayoutDebugOverlayPlan.Spec,
  ): SheetDelegate.FixedChildSpec["widthBehavior"] {
    if (spec.shape === "default" || spec.shape === "stacked") {
      return "fill";
    }
    return Math.max(1, spec.widthPx);
  }
}
